/**
 * Measurement: converting metric units, applying measurement in problems (length, mass, capacity, time).
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeNumericDistractors = (correct, count = 3) => {
    const base = Number(correct);
    const seen = new Set([String(correct)]);
    const out = [];
    let step;
    let offsets;
    if (base > 0 && base < 20 && !Number.isInteger(base)) {
        step = 0.25;
        offsets = [-0.5, -0.25, 0.25, 0.5, 1, -1];
    } else {
        step = base < 100 ? 5 : base < 1000 ? 20 : 50;
        offsets = [-step * 2, -step, step, step * 2, step + 1, step - 1];
    }
    while (out.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const val = base + off;
        if (val > 0 && !seen.has(String(val))) {
            seen.add(String(val));
            out.push(String(val));
        }
    }
    return out.slice(0, count);
};

/** Converting between metric units (length, mass, capacity, time) */
export const generateMetricConversionQuestion = (difficulty = "normal") => {
    const templates = [
        {
            q: (n) => `How many centimetres are in ${n} metres?`,
            ans: (n) => n * 100,
            gen: () => Math.floor(Math.random() * 9) + 2,
        },
        {
            q: (n) => `How many metres are in ${n} centimetres?`,
            ans: (n) => n / 100,
            gen: () => [100, 200, 300, 500, 400, 600, 800][Math.floor(Math.random() * 7)],
        },
        {
            q: (n) => `Convert ${n} km into metres.`,
            ans: (n) => n * 1000,
            gen: () => Math.floor(Math.random() * 8) + 1,
        },
        {
            q: (n) => `How many grams are in ${n} kg?`,
            ans: (n) => n * 1000,
            gen: () => Math.floor(Math.random() * 5) + 1,
        },
        {
            q: (n) => `Convert ${n} g to kg. Write as a decimal (e.g. 0.5).`,
            ans: (n) => (n / 1000).toString(),
            gen: () => [500, 250, 1000, 750, 200][Math.floor(Math.random() * 5)],
        },
        {
            q: (n) => `How many millilitres are in ${n} litres?`,
            ans: (n) => n * 1000,
            gen: () => Math.floor(Math.random() * 5) + 1,
        },
        {
            q: (n) => `Convert ${n} mL to litres. Write as a decimal (e.g. 0.5).`,
            ans: (n) => (n / 1000).toString(),
            gen: () => [500, 1000, 250, 750, 200][Math.floor(Math.random() * 5)],
        },
        {
            q: (n) => `How many minutes are in ${n} hours?`,
            ans: (n) => n * 60,
            gen: () => Math.floor(Math.random() * 5) + 1,
        },
        {
            q: (n) => `Convert ${n} minutes into hours. Give your answer as a decimal (e.g. 1.5).`,
            ans: (n) => (n / 60).toString(),
            gen: () => [30, 60, 90, 120, 45][Math.floor(Math.random() * 5)],
        },
    ];

    const t = templates[Math.floor(Math.random() * templates.length)];
    const n = t.gen();
    const answer = typeof t.ans(n) === "number" ? String(t.ans(n)) : t.ans(n);
    const question = t.q(n);
    const numVal = Number(answer) || parseFloat(answer);
    const distractorBase = Number.isInteger(numVal) ? numVal : parseFloat(answer);
    const options = shuffle([answer, ...makeNumericDistractors(distractorBase, 3)]);
    const unitMatch = question.match(/(centimetres|metres|km|grams|kg|millilitres|litres|minutes|hours)/gi);
    return {
        question,
        answer,
        options,
        type: "metric-conversion",
        mode: "conversion",
        visualType: "conversion-chart",
        value: n,
        units: unitMatch || [],
    };
};

/** Applying measurement concepts: length, mass, capacity, time in problems */
export const generateMeasurementProblemsQuestion = (difficulty = "normal") => {
    const problems = [
        { q: "A ribbon is 2 m 50 cm long. How many centimetres is that?", answer: "250" },
        { q: "A book weighs 1 kg 200 g. How many grams is that?", answer: "1200" },
        { q: "A bottle holds 2 L 500 mL. How many millilitres is that?", answer: "2500" },
        { q: "A film is 1 hour 45 minutes long. How many minutes is that?", answer: "105" },
        { q: "Tom runs 3 km. How many metres is that?", answer: "3000" },
        { q: "A parcel weighs 4 kg. How many grams is that?", answer: "4000" },
        { q: "A jug holds 750 mL. How many litres is that? Write as a decimal.", answer: "0.75" },
        { q: "A lesson lasts 90 minutes. How many hours is that? Give your answer as a decimal (e.g. 1.5).", answer: "1.5" },
        { q: "A desk is 120 cm long. How many metres is that? Write as a decimal.", answer: "1.2" },
        { q: "Sarah has 2 L of juice. She pours 500 mL out. How many mL are left?", answer: "1500" },
    ];

    const item = problems[Math.floor(Math.random() * problems.length)];
    const numVal = Number(item.answer) || parseFloat(item.answer);
    const options = shuffle([item.answer, ...makeNumericDistractors(numVal, 3)]);
    return {
        question: item.q,
        answer: item.answer,
        options,
        type: "measurement-problems",
        mode: "problems",
        visualType: "measurement-problem",
    };
};

export const generateMeasurementQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "metric-conversion":
            return generateMetricConversionQuestion(difficulty);
        case "measurement-problems":
            return generateMeasurementProblemsQuestion(difficulty);
        default:
            return generateMetricConversionQuestion(difficulty);
    }
};
